import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AppBar, Tabs, Tab, IconButton, Box } from '@mui/material';
import { ArrowLeft } from 'react-feather';
import { useTranslation } from 'react-i18next';
import Breadcrumb from 'src/components/Breadcrumb';
import TabPanel from 'src/components/TabPanel/TabPanel';
import { useNavigate, useParams } from 'react-router-dom';
import GeneralView from './GeneralDetail/General';
import Contributions from './ContributionsList/Contributions';
import ContractStatus, {
  ContractDetailTabsIndex
} from '../../../utils/constants/contracts';
import RentBalanceSchedule from './RentBalanceSchedule/RentBalanceSchedule';
import FiscalBalanceTabs from './FiscalBalanceTabs/FiscalBalanceTabs';
import AvalListView from './AvalListView/AvalListView';
import AccountStatementDetail from './AccountStatementDetail.js/AccountStatementDetail';
import ContractEvents from './ContractEvents/ContractEvents';
import AccountingBalanceView from './AccountingBalance';

export default function ContracTabs({ contract }) {
  const navigate = useNavigate();
  const { id, tab } = useParams();
  const { t } = useTranslation();
  const [value, setValue] = React.useState(false);

  const handleChange = useCallback(
    (_, newValue) => {
      setValue(newValue);
      navigate(`/contracts/${id}/${newValue}`);
    },
    [setValue]
  );

  useEffect(() => {
    if (id && !tab) {
      setValue(ContractDetailTabsIndex.DETAIL);
    }
    if (tab) {
      if (Object.values(ContractDetailTabsIndex).includes(tab)) {
        setValue(tab);
      } else {
        navigate(`/contracts/${id}`);
      }
    }
  }, [id, tab]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Breadcrumb
        translationCrumbNumber={[{ index: 1, translation: contract.folio }]}
      />
      <AppBar
        variant="outlined"
        position="static"
        sx={{
          border: 'none',
          borderBottom: '1px solid rgba(43,45,66,0.1)',
          background: 'transparent',
          flexDirection: 'row'
        }}
      >
        <IconButton onClick={() => navigate(-1)} color="primary" component="span">
          <ArrowLeft />
        </IconButton>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={value}
          onChange={handleChange}
        >
          <Tab
            label={t('COMMON.DETAILS')}
            value={ContractDetailTabsIndex.DETAIL}
          />
          <Tab
            label={t('COMMON.AVALS')}
            value={ContractDetailTabsIndex.AVALS}
          />
          <Tab
            label={t('COMMON.CONTRIBUTIONS')}
            value={ContractDetailTabsIndex.CONTRIBUTIONS}
            disabled={
              contract.status !== ContractStatus.IN_PROGRESS &&
              contract.status !== ContractStatus.ENDED &&
              contract.status !== ContractStatus.CEDED
            }
          />

          <Tab
            label={t('CONTRACT_VIEW.CONTRACT_DETAIL.FISCAL_BALANCE')}
            value={ContractDetailTabsIndex.FISCAL_BALANCE}
          />

          <Tab
            label={t('CONTRACT_VIEW.CONTRACT_DETAIL.ACCOUNTING_BALANCE.TITLE')}
            value={ContractDetailTabsIndex.ACCOUNTING_BALANCE}
          />

          <Tab
            label={t('CONTRACT_VIEW.CONTRACT_DETAIL.PAYMENT_SCHEDULE')}
            value={ContractDetailTabsIndex.PAYMENT_SCHEDULE}
          />
          <Tab
            label={t('CONTRACT_VIEW.CONTRACT_DETAIL.ACCOUNTING_STATE')}
            value={ContractDetailTabsIndex.ACCOUNTING_STATE}
          />
          <Tab
            label={t('CONTRACT_VIEW.CONTRACT_EVENTS')}
            value={ContractDetailTabsIndex.CONTRACT_EVENTS}
          />
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={ContractDetailTabsIndex.DETAIL}>
        <GeneralView contract={contract} />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.AVALS}>
        <AvalListView />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.CONTRIBUTIONS}>
        <Contributions contract={contract} />
      </TabPanel>

      <TabPanel
        value={value}
        index={ContractDetailTabsIndex.ACCOUNTING_BALANCE}
      >
        <AccountingBalanceView contract={contract} />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.FISCAL_BALANCE}>
        <FiscalBalanceTabs />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.PAYMENT_SCHEDULE}>
        <RentBalanceSchedule />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.ACCOUNTING_STATE}>
        <AccountStatementDetail />
      </TabPanel>

      <TabPanel value={value} index={ContractDetailTabsIndex.CONTRACT_EVENTS}>
        <ContractEvents />
      </TabPanel>
    </Box>
  );
}

ContracTabs.propTypes = {
  contract: PropTypes.object.isRequired
};
